import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '@/app/providers/AuthContext';
import { supabaseService, type InstallationStatus, type RepositoryMetrics as RepoMetrics, type Repository, type PullRequest, type DailyPrStat, type CodeReviewSummary } from '@/shared/services/supabaseService';
import { UserProfile } from '@/lib/supabase';

export interface PullRequestWithReview extends PullRequest {
  has_codii_review?: boolean;
  merge_time_hours?: number;
}

export const useDashboard = () => {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  const [userData, setUserData] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Graph/Metrics State
  const [graphLoading, setGraphLoading] = useState(true);
  const [repoMetrics, setRepoMetrics] = useState<RepoMetrics[]>([]);
  const [dailyStats, setDailyStats] = useState<DailyPrStat[]>([]);
  const [weeklyMetrics, setWeeklyMetrics] = useState<RepoMetrics | null>(null);

  // Data State
  const [repositories, setRepositories] = useState<Repository[]>([]);
  const [openPullRequests, setOpenPullRequests] = useState<PullRequestWithReview[] | null>(null);
  const [recentMerges, setRecentMerges] = useState<PullRequestWithReview[] | null>(null);
  const [recentReviews, setRecentReviews] = useState<CodeReviewSummary[]>([]);

  // UI State
  const [selectedRepoId, setSelectedRepoId] = useState<string | undefined>();
  const [installationStatus, setInstallationStatus] = useState<InstallationStatus | null>(null);
  const [activeTab, setActiveTab] = useState('overview');

  // Handle URL query parameters for tab selection
  useEffect(() => {
    const tabFromUrl = searchParams.get('tab');
    if (tabFromUrl && ['overview', 'repositories', 'settings'].includes(tabFromUrl)) {
      setActiveTab(tabFromUrl);
    }
  }, [searchParams]);

  const handleTabChange = (tab: string) => {
    setActiveTab(tab);
    setSearchParams({ tab });
  };

  // Initialize Dashboard Data
  useEffect(() => {
    const initializeDashboard = async () => {
      setLoading(true);
      setError(null);
      try {
        const status = await supabaseService.getInstallationStatus();
        setInstallationStatus(status);

        if (status.is_installed) {
          const [dashboardData, reposData] = await Promise.all([
            supabaseService.getDashboardData(),
            supabaseService.getRepositories()
          ]);

          const allPrs = [...(dashboardData.openPullRequests || []), ...(dashboardData.recentMerges || [])];
          const prIds = allPrs.map(pr => pr.pr_number);

          // Only fetch reviews if there are PRs to check
          let reviewStatus: Record<string, boolean> = {};
          if (prIds.length > 0) {
             reviewStatus = await supabaseService.getReviewsForPrs(prIds);
          }

          const openPrsWithStatus = (dashboardData.openPullRequests || []).map(pr => ({
            ...pr,
            has_codii_review: reviewStatus[pr.pr_number] || false,
          }));

          const recentMergesWithStatus = (dashboardData.recentMerges || []).map(pr => ({
            ...pr,
            has_codii_review: reviewStatus[pr.pr_number] || false,
            merge_time_hours: pr.merged_at && pr.created_at
              ? (new Date(pr.merged_at).getTime() - new Date(pr.created_at).getTime()) / (1000 * 60 * 60)
              : undefined,
          }));

          setUserData(dashboardData.userProfile);
          setRepoMetrics(dashboardData.repoMetrics);
          setOpenPullRequests(openPrsWithStatus);
          setRecentMerges(recentMergesWithStatus);
          setRecentReviews(dashboardData.reviews);
          setRepositories(reposData);
        }
      } catch (error) {
        console.error('Error initializing dashboard:', error);
        setError('Failed to load dashboard data. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    if (user?.id) {
      initializeDashboard();
    } else {
      setLoading(false);
    }
  }, [user]);

  // Fetch Daily Stats, Weekly Metrics, and Recent Reviews when repo or user changes
  useEffect(() => {
    const fetchStats = async () => {
      setGraphLoading(true);
      try {
        const [dailyStatsData, weeklyMetricsData, reviewsData] = await Promise.all([
          supabaseService.getDailyPrStats(selectedRepoId),
          supabaseService.getWeeklyMetrics(selectedRepoId),
          supabaseService.getCodeReviews(selectedRepoId)
        ]);
        console.debug(`Stats from Supabase for repo: ${selectedRepoId || 'all'}`, { dailyStatsData, weeklyMetricsData, reviewsData });
        setDailyStats(dailyStatsData);
        setWeeklyMetrics(weeklyMetricsData);
        setRecentReviews(reviewsData);
      } catch (error) {
        console.error('Error fetching stats:', error);
        setDailyStats([]);
        setWeeklyMetrics(null);
        setRecentReviews([]);
      } finally {
        setGraphLoading(false);
      }
    };

    if (user?.id && installationStatus?.is_installed) {
      fetchStats();
    }
  }, [user, selectedRepoId, installationStatus]);

  const simpleMetrics = weeklyMetrics;

  return {
    userData,
    loading,
    error,
    graphLoading,
    repoMetrics,
    repositories,
    openPullRequests,
    recentMerges,
    recentReviews,
    selectedRepoId,
    setSelectedRepoId,
    dailyStats,
    installationStatus,
    activeTab,
    handleTabChange,
    simpleMetrics
  };
};
